import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, PageState, StatusPill, Empty } from "@/components/app/Common";
import { ScoreChip } from "@/components/app/HealthGauge";
import { EntityForm } from "@/components/app/EntityForm";
import { useLoad } from "@/hooks/useLoad";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";

export default function Entities() {
  const { user } = useAuth();
  const state = useLoad(() => Promise.all([api.get("/entities"), api.get("/meta")]).then(([a, b]) => ({ ents: a.data, meta: b.data })));
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    if (params.get("new")) {
      setEdit(null);
      setOpen(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);
  const ents = state.data?.ents || [];
  const byId = Object.fromEntries(ents.map((e) => [e.id, e]));
  return (
    <div>
      <PageHeader eyebrow="Module 2" title="Entity management" subtitle={Array.isArray(user?.workspace?.entity_ids) ? `${ents.length} entit${ents.length === 1 ? "y" : "ies"} shared with you` : `${ents.length} of ${user?.limits?.entities} entities on your plan · Canada & USA jurisdictions`}>
        {!Array.isArray(user?.workspace?.entity_ids) && <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-entity-btn"><Plus className="h-4 w-4 mr-1" />Add corporation</Button>}
      </PageHeader>
      <PageState state={state}>
        {ents.length === 0 ? (
          <Empty icon={Building2} title="No entities yet" text="Add a corporation or LLC — AccruallQ generates its filing and tax calendar automatically." testId="entities-empty" />
        ) : (
          <div className="surface overflow-x-auto" data-testid="entities-table">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>{["Entity", "Jurisdiction", "Corp #", "BN / EIN", "Incorporated", "FYE", "Directors", "Status", "Score"].map((h) => <th key={h} className="text-left font-medium px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {ents.map((e) => (
                  <tr key={e.id} className="hover:bg-accent/50 cursor-pointer transition-colors" onClick={() => { setEdit(e); setOpen(true); }} data-testid={`entity-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{e.name}</div>
                      {e.parent_id && <div className="text-xs text-muted-foreground">↳ {byId[e.parent_id]?.name}</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{e.jurisdiction_label}</td>
                    <td className="px-4 py-3 font-mono text-xs">{e.corp_number || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{e.bn || e.ein || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(e.incorporation_date)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{e.fiscal_year_end}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-[180px] truncate">{(e.directors || []).join(", ") || "—"}</td>
                    <td className="px-4 py-3"><StatusPill status={e.status} /></td>
                    <td className="px-4 py-3"><ScoreChip score={e.score} band={e.band} testId={`entity-score-${e.id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageState>
      <EntityForm open={open} onOpenChange={setOpen} entity={edit} entities={ents} meta={state.data?.meta} onSaved={state.reload} />
    </div>
  );
}
